import { toResource } from '@schir-int-client/test-helpers';
import { isNil } from 'lodash-es';
import { Action, Store } from '@ngrx/store';
import { Resource } from '@ngxp/rest';

class Dummy {}

export function createDummyResource(linkRelations: string[] = []) {
	return toResource(new Dummy(), linkRelations);
}

export interface ListResource<T extends Resource, K extends string> extends Resource {
	_embedded: Record<K, T[]>;
}

export interface PagedListResource<T extends Resource, K extends string> extends ListResource<T, K> {
	page: {
		totalElements: number;
		size: number;
		totalPages: number;
	};
}

export interface StateResource<T> {
	resource: T;
	reload: boolean;
	loading: boolean;
	loaded: boolean;
}

export interface OwnedStateResource<T> {
	resource: T;
	reload: boolean;
	loading: boolean;
	loaded: boolean;
	owner: string;
}

export function createEmptyStateResource<T>(loading: boolean = false): StateResource<T> {
	return createStateResource(<T>null, loading);
}

export function createStateResource<T>(resource: T, loading: boolean = false): StateResource<T> {
	return { loading, loaded: !isNil(resource), reload: false, resource };
}

export function createEmptyOwnedStateResource<T>(owner: string = '', loading: boolean = false): OwnedStateResource<T> {
	return createOwnedStateResource(owner, <T>null, loading);
}

export function createOwnedStateResource<T>(
	owner: string,
	resource: T,
	loading: boolean = false
): OwnedStateResource<T> {
	return { loading, loaded: !isNil(resource), reload: false, resource, owner };
}

/**
 * Prevents parallel loading of same resource. Note that just using doIfLoadingRequired does not work because that method uses
 * attributes of the state which can be set only _after_ the load action is received. This class provides an own guard while retaining
 * the whole behaviour of doIfLoadingRequired for compatibility.
 */
export class LoadingGuard<T> {
	private currentlyRequestedUri: string;

	constructor(private store: Store) {}

	/**
	 * Triggers loading the resource if necessary.
	 * @param res resource to load if necessary
	 * @param actionProvider produces the action which indicates to load the resource
	 * @param uri identifies the currently loaded resource in case the action can load different values. It is not required that this value is indeed an uri!
	 * @return true if a reload is under way
	 */
	public mustLoadFirst(res: StateResource<T>, actionProvider: () => Action, uri = 'default'): boolean {
		const result = doIfLoadingRequired(res, () => {
			if (uri !== this.currentlyRequestedUri) {
				this.currentlyRequestedUri = uri;
				this.store.dispatch(actionProvider());
			}
		});
		if (res.loaded) {
			this.reset();
		}
		return result;
	}

	public mustLoadOwnedFirst(res: OwnedStateResource<T>, actionProvider: () => Action, owner: string): boolean {
		const result = doIfLoadingOfOwnedRequired(
			res,
			() => {
				if (owner !== this.currentlyRequestedUri) {
					this.currentlyRequestedUri = owner;
					this.store.dispatch(actionProvider());
				}
			},
			owner
		);
		if (res.loaded) {
			this.reset();
		}
		return result;
	}

	public reset() {
		this.currentlyRequestedUri = null;
	}
}

export function doIfLoadingRequired(stateResource: StateResource<any>, runnable: () => void): boolean {
	if (isLoadingRequired(stateResource)) {
		runnable();
		return true;
	}
	return false;
}

export function isLoadingRequired(stateResource: StateResource<any>): boolean {
	if (stateResource.loading) {
		return false;
	}
	if (stateResource.reload) {
		return true;
	}
	return !stateResource.loaded;
}

export function doIfLoadingOfOwnedRequired(
	stateResource: OwnedStateResource<any>,
	runnable: () => void,
	owner: string
): boolean {
	if (isLoadingOfOwnedRequired(stateResource, owner)) {
		runnable();
		return true;
	}
	return false;
}

export function isLoadingOfOwnedRequired(stateResource: OwnedStateResource<any>, owner: string): boolean {
	if (stateResource.owner !== owner) {
		return true;
	}
	if (stateResource.loading) {
		return false;
	}
	if (stateResource.reload) {
		return true;
	}
	return !stateResource.loaded;
}
